import { isEmbedded } from "@/lib/embedded";
import { regionFromPathname } from "@/lib/region";

const API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_ID || process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
const SERVER_ZONE = process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE === "EU" ? "EU" : "US";

export const ANALYTICS_EVENTS = {
  NAVIGATION_CLICKED: "NAVIGATION CLICKED",
  SEARCH_PERFORMED: "SEARCH PERFORMED",
  SEARCH_RESULT_CLICKED: "SEARCH RESULT CLICKED",
  BOOKING_STARTED: "BOOKING STARTED",
  BOOKING_ENGAGED: "BOOKING FORM ENGAGED",
  BOOKING_SUBMITTED: "BOOKING SUBMITTED",
  BOOKING_CONFIRMATION_VIEWED: "BOOKING CONFIRMATION VIEWED",
  QUIZ_STARTED: "SUITABILITY QUIZ STARTED",
  QUIZ_ANSWERED: "SUITABILITY QUIZ ANSWERED",
  QUIZ_COMPLETED: "SUITABILITY QUIZ COMPLETED",
  QUIZ_ABANDONED: "SUITABILITY QUIZ ABANDONED",
  CALLBACK_STARTED: "CALLBACK STARTED",
  CALLBACK_REQUESTED: "CALLBACK REQUESTED",
  FORM_SUBMISSION_FAILED: "FORM SUBMISSION FAILED",
  FAQ_WIDGET_OPENED: "FAQ WIDGET OPENED",
  FAQ_TOPIC_SELECTED: "FAQ TOPIC SELECTED",
  FAQ_OPENED: "FAQ OPENED",
  VIDEO_PLAYED: "VIDEO PLAYED",
  PROCEDURE_SELECTED: "PROCEDURE SELECTED",
  CLINIC_SELECTED: "CLINIC SELECTED",
  LCG_VIEWED: "LIFETIME CARE GUARANTEE VIEWED",
  NOT_FOUND: "404 ENCOUNTERED",
  PHONE_CLICKED: "PHONE CLICKED",
  OUTBOUND_LINK_CLICKED: "OUTBOUND LINK CLICKED",
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type EventProps = Record<string, string | number | boolean | undefined | null>;

type AmplitudeModule = typeof import("@amplitude/analytics-browser");

let sdk: AmplitudeModule | null = null;
let initialized = false;
let initPromise: Promise<void> | null = null;
let queue: Array<{ event: AnalyticsEvent; props?: EventProps }> = [];

export function analyticsEnabled(): boolean {
  return Boolean(API_KEY);
}

function clean(props?: EventProps): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props) return out;
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out;
}

export async function initAnalytics(userProps?: EventProps): Promise<void> {
  if (!API_KEY || typeof window === "undefined" || isEmbedded()) return;
  if (initialized) {
    sdk?.setOptOut(false);
    if (userProps) setUserProperties(userProps);
    return;
  }
  if (initPromise) return initPromise;

  initPromise = (async () => {
    sdk = await import("@amplitude/analytics-browser");
    sdk.init(API_KEY, {
      serverZone: SERVER_ZONE,
      enableDiagnostics: false,
      autocapture: {
        sessions: true,
        attribution: true,
        pageViews: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
    });
    initialized = true;
    if (userProps) setUserProperties(userProps);
    const pending = queue;
    queue = [];
    for (const item of pending) sdk.track(item.event, clean(item.props));
  })();

  return initPromise;
}

function withRegion(props?: EventProps): EventProps {
  return { region: regionFromPathname(window.location.pathname), ...props };
}

export function track(event: AnalyticsEvent, props?: EventProps): void {
  if (!API_KEY || typeof window === "undefined" || isEmbedded()) return;
  const enriched = withRegion(props);
  if (!initialized || !sdk) {
    if (queue.length < 50) queue.push({ event, props: enriched });
    return;
  }
  sdk.track(event, clean(enriched));
}

export function setUserProperties(props: EventProps): void {
  if (!initialized || !sdk) return;
  const identify = new sdk.Identify();
  for (const [key, value] of Object.entries(clean(props))) {
    identify.set(key, value);
  }
  sdk.identify(identify);
}

export function disableAnalytics(): void {
  queue = [];
  sdk?.setOptOut(true);
}
